import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDownToLine, ArrowUpFromLine, ClipboardCheck, Clock, Wallet } from 'lucide-react'
import {
  cashMovementSchema, closeShiftSchema, formatDateTime, formatMoney, formatNumber, toMinor,
} from '@najah/core'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Dialog, EmptyState, Input, Textarea,
  Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { shiftsApi } from '../lib/salesApi'
import { useFormErrors } from '../lib/useFormErrors'

function CloseShiftDialog({
  open, onClose, shiftId, expectedCash,
}: { open: boolean; onClose: () => void; shiftId: string; expectedCash: number }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, validate, handleApiError } = useFormErrors()
  const [counted, setCounted] = useState('')
  const [note, setNote] = useState('')

  const countedMinor = counted ? toMinor(Number(counted) || 0) : 0
  const difference = countedMinor - expectedCash

  const mutation = useMutation({
    mutationFn: () => {
      const parsed = validate(closeShiftSchema, { countedCash: countedMinor, note: note.trim() })
      if (!parsed) throw new Error('VALIDATION')
      return shiftsApi.close(shiftId, parsed)
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['shift'] })
      const label =
        res.difference === 0 ? 'الدرج مطابق تماماً'
          : res.difference > 0 ? `زيادة ${formatMoney(res.difference)}`
          : `عجز ${formatMoney(Math.abs(res.difference))}`
      toast.success('أُغلقت الوردية', label)
      setCounted('')
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر إغلاق الوردية')
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title="إغلاق الوردية"
      description="عُدّ النقد في الدرج الآن"
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!counted}>
            إغلاق
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-bg-subtle px-4 py-3 text-sm">
          المتوقّع في الدرج:{' '}
          <span className="font-semibold text-fg" dir="ltr">{formatMoney(expectedCash)}</span>
        </div>

        <Input
          label="النقد المعدود فعلياً" required type="number" min="0" step="0.01" ltr autoFocus
          value={counted}
          onChange={(e) => setCounted(e.target.value)}
          error={errors.countedCash}
          hint="دج"
        />

        {counted && (
          <Alert tone={difference === 0 ? 'success' : Math.abs(difference) > 10000 ? 'danger' : 'warning'}>
            {difference === 0 ? (
              'الدرج مطابق تماماً'
            ) : difference > 0 ? (
              <>زيادة قدرها <span className="font-semibold" dir="ltr">{formatMoney(difference)}</span></>
            ) : (
              <>عجز قدره <span className="font-semibold" dir="ltr">{formatMoney(Math.abs(difference))}</span></>
            )}
          </Alert>
        )}

        <Textarea label="ملاحظة" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  )
}

function CashDialog({
  open, onClose, shiftId, type,
}: { open: boolean; onClose: () => void; shiftId: string; type: 'DEPOSIT' | 'WITHDRAWAL' }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, validate, handleApiError } = useFormErrors()
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')

  const mutation = useMutation({
    mutationFn: () => {
      const parsed = validate(cashMovementSchema, { type, amount: toMinor(Number(amount) || 0), note: note.trim() })
      if (!parsed) throw new Error('VALIDATION')
      return shiftsApi.cash(shiftId, parsed)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shift'] })
      toast.success(type === 'DEPOSIT' ? 'تمّ الإيداع' : 'تمّ السحب')
      setAmount('')
      setNote('')
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err)
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={type === 'DEPOSIT' ? 'إيداع نقد في الدرج' : 'سحب نقد من الدرج'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>تسجيل</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="المبلغ" required type="number" min="0" step="0.01" ltr autoFocus
          value={amount} onChange={(e) => setAmount(e.target.value)}
          error={errors.amount} hint="دج"
        />
        <Textarea label="السبب" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  )
}

export function ShiftsPage() {
  const { data, isPending } = useQuery({ queryKey: ['shift'], queryFn: shiftsApi.current })
  const { data: history, isPending: historyPending } = useQuery({
    queryKey: ['shift', 'history'],
    queryFn: () => shiftsApi.history(1, 25),
  })

  const [closeOpen, setCloseOpen] = useState(false)
  const [cashType, setCashType] = useState<'DEPOSIT' | 'WITHDRAWAL' | null>(null)

  const shift = data?.shift

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-fg">الورديات والصندوق</h1>
        <p className="mt-0.5 text-sm text-fg-muted">فتح وإغلاق الصندوق وجرد النقد</p>
      </div>

      {isPending ? (
        <Card><CardBody><span className="block h-24 animate-pulse rounded bg-bg-subtle" /></CardBody></Card>
      ) : shift ? (
        <Card>
          <CardHeader
            title="الوردية الحالية"
            description={`${shift.registerName} · فُتحت ${formatDateTime(shift.openedAt)}`}
            action={<Badge tone="success" dot>مفتوحة</Badge>}
          />
          <CardBody className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-4">
              {[
                { label: 'الرصيد الافتتاحي', value: formatMoney(shift.openingCash), icon: Wallet },
                { label: 'المبيعات', value: formatMoney(shift.salesTotal), icon: ClipboardCheck },
                { label: 'عدد الفواتير', value: formatNumber(shift.salesCount), icon: Clock },
                { label: 'المتوقّع في الدرج', value: formatMoney(shift.expectedCash), icon: Wallet },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-bg-subtle px-4 py-3">
                  <p className="text-xs text-fg-subtle">{stat.label}</p>
                  <p className="mt-0.5 font-bold text-fg" dir="ltr">{stat.value}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => setCashType('DEPOSIT')}
                startIcon={<ArrowDownToLine className="size-4" />}
              >
                إيداع
              </Button>
              <Button
                variant="secondary"
                onClick={() => setCashType('WITHDRAWAL')}
                startIcon={<ArrowUpFromLine className="size-4" />}
              >
                سحب
              </Button>
              <Button variant="danger" onClick={() => setCloseOpen(true)} className="ms-auto">
                إغلاق الوردية
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Alert tone="info" title="لا توجد وردية مفتوحة">
          افتح وردية من شاشة نقطة البيع لبدء العمل.
        </Alert>
      )}

      <Card>
        <CardHeader title="سجل الورديات" description="الفروقات بين المعدود والمتوقّع" />
        <TableWrapper className="rounded-none border-0 border-t">
          <Table>
            <Thead>
              <tr>
                <Th>الصندوق</Th>
                <Th>الموظّف</Th>
                <Th>الفتح</Th>
                <Th>الإغلاق</Th>
                <Th align="end">المتوقّع</Th>
                <Th align="end">المعدود</Th>
                <Th align="end">الفرق</Th>
              </tr>
            </Thead>
            {historyPending ? (
              <TableSkeleton rows={5} cols={7} />
            ) : (
              <Tbody>
                {history?.items.map((s) => (
                  <Tr key={s.id}>
                    <Td className="font-medium text-fg">{s.registerName}</Td>
                    <Td className="text-fg-muted">{s.userName ?? '—'}</Td>
                    <Td className="whitespace-nowrap text-xs text-fg-muted">{formatDateTime(s.openedAt)}</Td>
                    <Td className="whitespace-nowrap text-xs text-fg-muted">
                      {s.closedAt ? formatDateTime(s.closedAt) : <Badge tone="success" dot>مفتوحة</Badge>}
                    </Td>
                    <Td align="end" className="tabular-nums" dir="ltr">
                      {s.expectedCash != null ? formatMoney(s.expectedCash) : '—'}
                    </Td>
                    <Td align="end" className="tabular-nums" dir="ltr">
                      {s.countedCash != null ? formatMoney(s.countedCash) : '—'}
                    </Td>
                    <Td align="end" className="tabular-nums font-semibold" dir="ltr">
                      {s.difference == null ? (
                        '—'
                      ) : (
                        <span className={cn(s.difference === 0 ? 'text-success' : s.difference > 0 ? 'text-warning' : 'text-danger')}>
                          {s.difference > 0 ? '+' : ''}{formatMoney(s.difference)}
                        </span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            )}
          </Table>
          {!historyPending && history?.items.length === 0 && (
            <EmptyState icon={<Clock className="size-9" />} title="لا توجد ورديات بعد" />
          )}
        </TableWrapper>
      </Card>

      {shift && (
        <>
          <CloseShiftDialog
            open={closeOpen}
            onClose={() => setCloseOpen(false)}
            shiftId={shift.id}
            expectedCash={shift.expectedCash}
          />
          <CashDialog
            open={!!cashType}
            onClose={() => setCashType(null)}
            shiftId={shift.id}
            type={cashType ?? 'DEPOSIT'}
          />
        </>
      )}
    </div>
  )
}
